import React, { forwardRef, useCallback } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import styles from './AcimaButton.module.scss';
import messages from './messages';

import acimaLogo from '../../assets/images/svg/acima-logo.svg';
import { ACIMA_IFRAME_URL, ACIMA_MERCHANT_ID }from './constants';
import { CartInStore } from '../../slices/cart/types';
import { AcimaClient,CheckoutResponse, AcimaButtonProps } from './types';
import { paymentMethods } from '../../slices/payments';

const AcimaButton = forwardRef<HTMLButtonElement, AcimaButtonProps>((
  {
    onSuccess,
    isLoading,
    onModalError,
  },
  ref?
) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const cart = useSelector((state: { cart: CartInStore }) => state.cart);

  const getCustomerDetails = useCallback(() => ({
    firstName: cart.billingAddress?.firstName ?? '',
    lastName: cart.billingAddress?.lastName ?? '',
    email: cart.billingAddress?.email ?? '',
    phone: cart.billingAddress?.phone ?? '',
    address: {
      street1: cart.billingAddress?.streetName ?? '',
      street2: cart.billingAddress?.additionalStreetInfo ?? '',
      city: cart.billingAddress?.city ?? '',
      state: cart.billingAddress?.state ?? '',
      postalCode: cart.billingAddress?.postalCode ?? '',
    },
  }), [cart.billingAddress]);

  const getTransactionDetails = useCallback(() => {
    const items = cart.lineItems.map((item) => ({
      productId: item.productId,
      productName: item.name['en-US'] || item.name.en,
      unitPrice: item.totalPrice.centAmount,
      quantity: item.quantity,
    }));
    const reducedShipping = cart.customLineItems.reduce(
      (total, item) => total + item.money.centAmount, 0
    );
    return {
      lineItems: items,
      salesTax: 0,
      discounts: 0,
      shipping: reducedShipping ?? 0,
      cartTotalWithTax: cart.totalPrice.centAmount ?? 0,
    };
  }, [cart.lineItems, cart.customLineItems, cart.totalPrice]);

  const handleOnClick = useCallback(() => {
    if (window.Acima) {
      const acima: AcimaClient = new window.Acima.Client({
        merchantId: ACIMA_MERCHANT_ID,
        iframeUrl: ACIMA_IFRAME_URL,
      });
      
      const customer = getCustomerDetails();
      const transaction = getTransactionDetails();

      if (acima) {
        acima.checkout({
          customer,
          transaction,
        })
        .then((response: CheckoutResponse) => {
          if (response === null) {
            throw new Error('User Closed Acima');
          } else {
            const result = {
              ...response,
              transaction,
              address: customer.address,
            };
            onSuccess(result);
          }     
        }).catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Acima Iframe Error', error);
          if (onModalError) {
            dispatch({
              type: 'setStatus',
              status: 'idle',
            });
            dispatch({
              type: 'setPaymentStatus',
              status: 'idle'
            });
            onModalError(paymentMethods.CREDIT_CARD);
          }
        });
      }
    }
  }, [dispatch, getCustomerDetails, getTransactionDetails, onModalError, onSuccess]);


  return (
    <div className={styles['acima-button-wrapper']}>
      <div className={styles['acima-button']} />
      <button
        type="button"
        onClick={handleOnClick}
        className={classnames(styles['custom-acima-button'], 'btn', {
          [styles.disabled]: isLoading,
        })}
        id={formatMessage(messages.acimaButtonId)}
        ref={ref}
      >
        <div className={styles['img-container']}>
          <img
            alt={formatMessage(messages.acimaLogoAlt)}
            src={acimaLogo as unknown as string}
          />
        </div>
      </button>
    </div>
  );
});

export default AcimaButton;
